import {
  PLATFORM_USER_ACCESS_FILTER_ALL,
  PLATFORM_USER_ACCESS_UI,
} from "pages/PlatformUserAccess/PlatformUserAccess.constants";
import {
  CUSTOMER_ACCESS_STATUS,
  CUSTOMER_ACCESS_STATUS_OPTIONS,
  CUSTOMER_ACCESS_FILTER_OPTIONS,
  CUSTOMER_ACCESS_STATUS_RAW,
  isCustomerAccessStatus,
  normalizeCustomerAccessStatus,
} from "common/Constants/AccessStatus.constants";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export const USERS_PAGE_CONSTANTS = {
  // MFA_CONSENT:
  //   "By providing your mobile number, you consent to receive text messages from Inframark for account security, including Multi-Factor Authentication (MFA) codes. Message frequency varies. Reply STOP to unsubscribe. Standard message rates may apply.",
  SELECT_CUSTOMER_REQUIRED_ERROR:
    "Access restricted: You need to select a customer before proceeding.",
  MISSING_CUSTOMER_ID_ERROR:
    "Access restricted: Customer identifier is missing. Please select a customer from the header.",
  PAGE_TITLE: "User management",
  PAGE_SUBTITLE: "Users, roles and license seats.",
  INVITE_USER_LABEL: "Invite user",
  CLEAR_FILTERS_LABEL: "Clear filters",
  EMPTY_STATE_TITLE: "No users match these filters",
  EMPTY_STATE_HINT: "Clear one or more filters to widen the search.",
};

export const DEFAULT_CUSTOMER_ROLE_HINTS = {
  supportadmin: "Customer administration — users and data.",
  support_admin: "Customer administration — users and data.",
  admin: "Customer administration — users only.",
  supervisor: "Full operational access.",
  operator: "Operational access.",
  viewonly: "Read-only access.",
  view_only: "Read-only access.",
};

export const normalizeRoleHintKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const resolveRoleHint = (
  option = {},
  hintMap = DEFAULT_CUSTOMER_ROLE_HINTS
) => {
  if (option.hint) return String(option.hint);
  const keys = [option.value, option.label, option.id]
    .filter(Boolean)
    .map(normalizeRoleHintKey);
  for (const key of keys) {
    if (hintMap[key]) return hintMap[key];
    const compact = key.replace(/_/g, "");
    if (hintMap[compact]) return hintMap[compact];
  }
  return "";
};

export const CUSTOMER_INVITE_UI = {
  title: "Invite user",
  emailLabel: "Email address",
  emailPlaceholder: "name@inframark.com",
  emailHint:
    "",
  rolesLabel: "Roles",
  rolesEmptyHint: "",
  submitLabel: "Send invite",
  description: (tenantName) =>
    `The user will get an email invitation to join ${tenantName || "this customer"}.`,
};

export { PLATFORM_USER_ACCESS_FILTER_ALL as CUSTOMER_USERS_FILTER_ALL };

export {
  CUSTOMER_ACCESS_STATUS,
  CUSTOMER_ACCESS_STATUS_RAW,
  isCustomerAccessStatus,
  normalizeCustomerAccessStatus,
};

export const DEFAULT_CUSTOMER_USERS_LIST_FILTERS = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  role: "",
  accessStatus: "",
  accountStatus: "",
};

export const CUSTOMER_USERS_ACCESS_STATUS_FILTER_OPTIONS =
  CUSTOMER_ACCESS_FILTER_OPTIONS;

export const normalizeCustomerUsersListFilters = (rawColumnFilters = {}) => {
  const clean = (value) => {
    if (
      value == null ||
      value === "" ||
      value === PLATFORM_USER_ACCESS_FILTER_ALL
    ) {
      return "";
    }
    return String(value).trim();
  };
  return {
    email: clean(rawColumnFilters.email),
    firstName: clean(rawColumnFilters.firstName),
    lastName: clean(rawColumnFilters.lastName),
    phone: clean(rawColumnFilters.phone),
    role: clean(rawColumnFilters.role ?? rawColumnFilters.roles),
    accessStatus: clean(
      rawColumnFilters.accessStatus ?? rawColumnFilters.customerAccess
    ),
    accountStatus: clean(rawColumnFilters.accountStatus),
  };
};

export const mapCustomerUsersListFiltersToColumnFilters = (
  listFilters = DEFAULT_CUSTOMER_USERS_LIST_FILTERS
) => ({
  email: listFilters.email ?? "",
  firstName: listFilters.firstName ?? "",
  lastName: listFilters.lastName ?? "",
  phone: listFilters.phone ?? "",
  role: listFilters.role ?? "",
  accessStatus: listFilters.accessStatus ?? "",
  accountStatus: listFilters.accountStatus ?? "",
});

export const CUSTOMER_USER_ACCESS_OPERATION = {
  ASSIGN_ROLES: "ASSIGN_ROLES",
  GRANT: "GRANT",
  REVOKE: "REVOKE",
  RESTORE: "RESTORE",
};

export const CUSTOMER_USER_LIST_ACTION = {
  REVOKE_ACCESS: "revoke",
  RESTORE_ACCESS: "restore",
  RESEND: "resend",
};

const isEditableCustomerUserStatus = (status) => {
  const normalized = normalizeCustomerAccessStatus(status);
  return (
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.ACTIVE) ||
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.SUSPENDED) ||
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.REVOKED)
  );
};

export const isCustomerUserRolesEditable = (user) =>
  isEditableCustomerUserStatus(user?.accountStatus) &&
  isEditableCustomerUserStatus(user?.accessStatus);

export const CUSTOMER_USER_ROLES_REQUIRED_ERROR =
  PLATFORM_USER_ACCESS_UI.customerAccessRolesRequiredError;

export const validateCustomerUserRolesSelection = (roles) => {
  const normalized = (Array.isArray(roles) ? roles : [])
    .map((role) => String(role).trim())
    .filter(Boolean);
  if (normalized.length === 0) {
    return CUSTOMER_USER_ROLES_REQUIRED_ERROR;
  }
  return null;
};

const normalizeRoleName = (role) => String(role ?? "").trim();

const grantableOptionValues = (grantableOptions = []) =>
  new Set(
    (grantableOptions ?? [])
      .map((option) => normalizeRoleName(option?.value))
      .filter(Boolean)
  );

export const getLockedRolesForSelection = (assignedRoles, grantableOptions = []) => {
  const grantableValues = grantableOptionValues(grantableOptions);
  return (Array.isArray(assignedRoles) ? assignedRoles : [])
    .map(normalizeRoleName)
    .filter((role) => role && !grantableValues.has(role));
};

export const mergeRolesWithLockedRoles = (nextSelection, lockedRoles = []) => {
  const locked = (Array.isArray(lockedRoles) ? lockedRoles : [])
    .map(normalizeRoleName)
    .filter(Boolean);
  const selected = (Array.isArray(nextSelection) ? nextSelection : [])
    .map(normalizeRoleName)
    .filter(Boolean);
  return [...new Set([...locked, ...selected])];
};

export const buildRowRoleDropdownOptions = (
  grantableOptions = [],
  lockedRoles = [],
  roleLabelMap = {}
) => {
  const lockedOptions = (Array.isArray(lockedRoles) ? lockedRoles : [])
    .map(normalizeRoleName)
    .filter(Boolean)
    .map((value) => ({
      value,
      label: roleLabelMap[value] ?? value,
      locked: true,
    }));

  const editableOptions = (grantableOptions ?? [])
    .map((option) => ({
      value: normalizeRoleName(option?.value),
      label: String(option?.label ?? option?.value ?? "").trim(),
      locked: false,
    }))
    .filter((option) => option.value)
    .filter(
      (option, index, list) =>
        list.findIndex((item) => item.value === option.value) === index
    )
    .filter(
      (option) =>
        !lockedOptions.some((lockedOption) => lockedOption.value === option.value)
    );

  return [...lockedOptions, ...editableOptions];
};

export const formatRoleSelectionDisplay = (
  selectedRoles = [],
  dropdownOptions = [],
  roleLabelMap = {}
) =>
  (Array.isArray(selectedRoles) ? selectedRoles : [])
    .map(normalizeRoleName)
    .filter(Boolean)
    .map((value) => {
      const fromOption = dropdownOptions.find(
        (option) => normalizeRoleName(option?.value) === value
      );
      return fromOption?.label ?? roleLabelMap[value] ?? value;
    })
    .join(", ");

export const deriveCustomerUserAccessVariant = (user) => {
  const raw = String(user?.accessStatus ?? user?.accountStatus ?? "").trim();
  const lower = raw.toLowerCase();
  if (
    lower === CUSTOMER_ACCESS_STATUS_RAW.REVOKED.toLowerCase() ||
    lower === CUSTOMER_ACCESS_STATUS.SUSPENDED.toLowerCase()
  ) {
    return CUSTOMER_ACCESS_STATUS.REVOKED;
  }
  if (
    lower === CUSTOMER_ACCESS_STATUS.INVITED.toLowerCase() ||
    lower === CUSTOMER_ACCESS_STATUS_RAW.PENDING.toLowerCase()
  ) {
    return CUSTOMER_ACCESS_STATUS.INVITED;
  }
  return CUSTOMER_ACCESS_STATUS.ACTIVE;
};

export const isCustomerUserEmailClickable = (user) =>
  !isCustomerAccessStatus(
    deriveCustomerUserAccessVariant(user),
    CUSTOMER_ACCESS_STATUS.INVITED
  );

export const getCustomerUserListRowAction = (user) => {
  const variant = deriveCustomerUserAccessVariant(user);
  if (isCustomerAccessStatus(variant, CUSTOMER_ACCESS_STATUS.ACTIVE)) {
    return {
      label: "Revoke access",
      action: CUSTOMER_USER_LIST_ACTION.REVOKE_ACCESS,
      tone: "danger",
    };
  }
  if (isCustomerAccessStatus(variant, CUSTOMER_ACCESS_STATUS.REVOKED)) {
    return {
      label: "Restore access",
      action: CUSTOMER_USER_LIST_ACTION.RESTORE_ACCESS,
      tone: "primary",
    };
  }
  if (isCustomerAccessStatus(variant, CUSTOMER_ACCESS_STATUS.INVITED)) {
    return {
      label: "Resend invite",
      action: CUSTOMER_USER_LIST_ACTION.RESEND,
      tone: "primary",
    };
  }
  return null;
};
export const getCustomerRowAction = (accessStatus) => {
  if (accessStatus === CUSTOMER_ACCESS_STATUS.ACTIVE) {
    return {
      label: "Revoke access",
      action: CUSTOMER_USER_LIST_ACTION.REVOKE_ACCESS,
      tone: "danger",
    };
  }
  if (accessStatus === CUSTOMER_ACCESS_STATUS.REVOKED) {
    return {
      label: "Restore access",
      action: CUSTOMER_USER_LIST_ACTION.RESTORE_ACCESS,
      tone: "primary",
    };
  }
  return null;
};

/** Map membership / access status labels to StatusPill tones. */
export const getMembershipStatusTone = (status) => {
  const normalized = String(status ?? "").trim().toLowerCase();
  if (normalized === "active" || normalized === "accepted") return "ok";
  if (normalized === "invited") return "info";
  if (normalized === "expired" || normalized === "suspended") return "warn";
  if (normalized === "revoked") return "crit";
  return "neutral";
};

/**
 * Account status display — Invited maps to Pending (unaccepted invite),
 * matching WMP User Management UI v1.0.
 */
export const normalizeAccountStatusDisplay = (status) => {
  const raw = String(status ?? "").trim();
  const normalized = raw.toLowerCase();
  if (!raw) return "";
  if (normalized === "invited") return "Pending";
  if (normalized === "locked") return "Locked out";
  return raw;
};

/** Map account status labels to StatusPill tones. */
export const getAccountStatusTone = (status) => {
  const display = normalizeAccountStatusDisplay(status);
  const normalized = display.toLowerCase();
  if (normalized === "active") return "ok";
  if (normalized === "pending") return "neutral";
  if (normalized === "suspended") return "warn";
  if (normalized === "locked out" || normalized === "revoked") return "crit";
  return "neutral";
};

export const CUSTOMER_USERS_ACCOUNT_STATUS_FILTER_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Active", value: "Active" },
  { label: "Suspended", value: "Suspended" },
  { label: "Locked out", value: "Locked out" },
];

export const buildUsersResultSummary = (
  shownCount,
  filtersActive,
  grandTotal
) => {
  const shown = Number(shownCount) || 0;
  const total = Number(grandTotal) || 0;
  if (filtersActive) {
    if (total > 0) {
      return `${shown} of ${total} users match`;
    }
    return `${shown} users match`;
  }
  return `${shown} users`;
};

/**
 * Format phone for table display — US/CA as +1 (XXX) XXX-XXXX,
 * others via international spacing (e.g. +91 73876 84666).
 */
export const formatDisplayPhone = (rawPhone) => {
  const trimmed = String(rawPhone ?? "").trim();
  if (!trimmed) return "";
  try {
    const digits = trimmed.replace(/[^\d+]/g, "");
    const parsed =
      parsePhoneNumberFromString(trimmed) ||
      parsePhoneNumberFromString(
        trimmed.startsWith("+") ? trimmed : `+${trimmed.replace(/\D/g, "")}`
      ) ||
      (digits ? parsePhoneNumberFromString(digits) : null);
    if (!parsed || !parsed.isValid()) {
      return trimmed;
    }
    if (parsed.country === "US" || parsed.country === "CA") {
      const national = parsed.nationalNumber;
      if (national.length === 10) {
        return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
      }
    }
    return parsed.formatInternational();
  } catch {
    return trimmed;
  }
};